import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { Card, Spinner, Container, Row, Col, Badge, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import slugify from 'slugify';
import './PetDetail.css'; // Import CSS for responsive layout
import { Breadcrumb } from 'react-bootstrap';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faSearch, faPaw, faHome, faTag, faHeart } from '@fortawesome/free-solid-svg-icons';

function PetDetail() {
    const { id, slug } = useParams(); // Get the ID and slug from the URL params
    const [pet, setPet] = useState(null); // State to hold the pet details
    const [loading, setLoading] = useState(true); // Loading state
    const [error, setError] = useState(null); // Error state
    const [relatedPets, setRelatedPets] = useState([]); // State for related pets
    const { lang } = useParams();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchPetDetails = async () => {
            try {
                const response = await fetch(
                    `https://us-central1-petshome-da28b.cloudfunctions.net/api/public/pets/ad?id=${id}`
                );
                const data = await response.json();

                // If the API call was successful but pet data is missing or invalid, redirect to the home page.
                if (response.ok && data) {
                    // For example, if title is a required field and it's missing, consider it invalid.
                    if (!data.title) {
                        navigate('/', { replace: true });
                        return;
                    }
                    setPet(data);

                    // Fetch related pets after getting the main pet data
                    if (data.region || data.city || data.countryName) {
                        fetchRelatedPets(data);
                    }
                } else {
                    // On error from API or if data is missing, redirect to home.
                    navigate('/', { replace: true });
                    return;
                }
            } catch (err) {
                // In case of fetch error, redirect to home.
                navigate('/', { replace: true });
                return;
            } finally {
                setLoading(false);
            }
        };

        fetchPetDetails();
    }, [id, navigate]);

    // Function to fetch related pets based on location
    const fetchRelatedPets = async (petData) => {
        try {
            // Create query based on location information - prioritize city, then region, then country
            let locationQuery = '';
            if (petData.region) {
                locationQuery = `region=${encodeURIComponent(petData.region)}`;
            } else if (petData.countryName) {
                locationQuery = `country=${encodeURIComponent(petData.countryName)}`;
            }

            if (locationQuery) {
                const response = await fetch(
                    `https://us-central1-petshome-da28b.cloudfunctions.net/api/public/pets/all?${locationQuery}&limit=6`
                );
                if (response.ok) {
                    const data = await response.json();
                    // Filter out the current pet and limit to 5 related pets
                    const filtered = data.filter(relatedPet => relatedPet.id !== id).slice(0, 5);
                    setRelatedPets(filtered);
                }
            }
        } catch (err) {
            console.error("Error fetching related pets:", err);
        }
    };

    // Helper: create a URL-friendly slug for a string
    const toSlug = (str) => {
        if (!str) return '';
        return slugify(str, { lower: true });
    };

    if (loading) {
        return (
            <Container className='text-center'>
                <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    // If pet data is not available after loading (though redirection should occur), return null.
    if (!pet) {
        return null;
    }

    // Destructure pet object with potential new location fields.
    const {
        title,
        pet_breed,
        main_image,
        author_country_code,
        asking_price,
        description,
        countryName,
        region,
        city,
        pet_type, // Pet type if available (dog, cat, bird, etc.)
    } = pet;

    // Determine the pet type based on data or infer from breed if possible
    const determinePetType = () => {
        if (pet_type) return pet_type.toLowerCase();

        // Try to infer pet type from breed or title
        const petText = (pet_breed || '' + ' ' + title || '').toLowerCase();
        if (petText.includes('dog') || petText.includes('puppy') ||
            petText.includes('yorkie') || petText.includes('terrier') ||
            petText.includes('shepherd') || petText.includes('retriever')) {
            return 'dog';
        }
        if (petText.includes('cat') || petText.includes('kitten') ||
            petText.includes('persian') || petText.includes('siamese')) {
            return 'cat';
        }
        if (petText.includes('bird') || petText.includes('parrot') ||
            petText.includes('finch') || petText.includes('canary')) {
            return 'bird';
        }
        if (petText.includes('rabbit') || petText.includes('bunny')) {
            return 'rabbit';
        }
        if (petText.includes('fish') || petText.includes('aquarium')) {
            return 'fish';
        }
        if (petText.includes('reptile') || petText.includes('snake') ||
            petText.includes('lizard') || petText.includes('turtle')) {
            return 'reptile';
        }
        // Default to a generic pet type if we can't determine
        return 'pet';
    };

    const petType = determinePetType();

    // Use appropriate young animal term based on pet type
    const getYoungTerm = () => {
        switch (petType) {
            case 'dog': return 'puppy';
            case 'cat': return 'kitten';
            case 'rabbit': return 'baby rabbit';
            case 'bird': return 'baby bird';
            case 'fish': return 'juvenile fish';
            case 'reptile': return 'baby reptile';
            default: return 'young pet';
        }
    };

    const youngTerm = getYoungTerm();

    // Build a location string if any location data is provided.
    const locationString =
        countryName || region || city
            ? `${city ? city + ', ' : ''}${region ? region + ', ' : ''}${countryName ? countryName : ''
                }`.replace(/,\s*$/, '')
            : '';

    // Determine if this is likely an adoption or sale listing based on price
    const isAdoption = asking_price <= 300; // Assuming lower prices are adoption/rehoming fees
    const listingType = isAdoption ? "adoption" : "for sale";
    const listingAction = isAdoption ? "Adopt" : "Buy";

    // Build the dynamic page title with sale/adoption keywords
    const pageTitle = locationString
        ? `${listingAction} ${pet_breed || petType} ${title} - ${listingType} in ${locationString} | Pets Home App`
        : `${listingAction} ${pet_breed || petType} ${title} - ${listingType} | Pets Home App`;

    // Build a canonical URL path using the new location fields if all are available; otherwise, use the existing location.pathname.
    const canonicalPath =
        countryName && region && city
            ? `/${toSlug(countryName)}/${toSlug(region)}/${toSlug(city)}/${slug}`
            : location.pathname;

    // Generate generic related search terms based on pet data with enhanced sale/adoption keywords
    const relatedSearchTerms = [
        `${listingAction} ${pet_breed || petType} ${city || region || countryName || ''}`,
        `${pet_breed || petType} ${listingType} in ${city || region || countryName || ''}`,
        `${isAdoption ? 'Rescue' : ''} ${pet_breed || petType} ${region || countryName || ''}`,
        `${pet_breed || petType} ${isAdoption ? 'adoption' : 'breeder'} ${city || region || countryName || ''}`,
        `${pet_breed || petType}s near me ${city || region || ''}`,
        `${isAdoption ? 'Adopt' : 'Buy'} ${pet_breed || ''} ${youngTerm} ${city || region || countryName || ''}`
    ].filter(term => term.trim().length > 0);

    // Generate URLs for location browsing
    const countryUrl = countryName ? `/pet-classified-ads/${toSlug(countryName)}` : null;
    const regionUrl = countryName && region ? `/pet-classified-ads/${toSlug(countryName)}/${toSlug(region)}` : null;
    const cityUrl = countryName && region && city ? `/pet-classified-ads/${toSlug(countryName)}/${toSlug(region)}/${toSlug(city)}` : null;

    // Create a list of related search variations that are appropriate for any pet type
    const relatedVariations = pet_breed ? [
        pet_breed,
        `${pet_breed} ${youngTerm}`,
        `${pet_breed} for ${isAdoption ? 'adoption' : 'sale'}`,
        `${pet_breed} ${petType}`,
        `Purebred ${pet_breed}`,
        `${isAdoption ? 'Rescue' : 'Pedigree'} ${pet_breed}`
    ] : [
        petType,
        `${petType} for ${isAdoption ? 'adoption' : 'sale'}`,
        `${petType}s in ${city || region || countryName || ''}`,
        `${youngTerm} for ${isAdoption ? 'adoption' : 'sale'}`,
        `${isAdoption ? 'Rescued' : 'Purebred'} ${petType}`
    ];

    return (
        <>
            <Helmet>
                <link rel="canonical" href={`https://www.petshome.app${canonicalPath}${location.search}`} />
                <title>{pageTitle}</title>
                <meta name="description" content={`${listingAction} this ${pet_breed || petType} ${title} ${listingType} in ${locationString || 'various locations'}. ${isAdoption ? 'Adopt, rescue, or rehome' : 'Purchase'} your perfect ${petType} companion. ${description ? description.substring(0, 100) + '...' : 'View details on Pets Home App.'}`} />
                <meta name="keywords" content={`${title}, ${pet_breed || ''}, ${petType}, ${locationString}, ${pet_breed || petType} ${listingType}, ${listingAction.toLowerCase()} ${pet_breed || petType}, ${youngTerm}, ${isAdoption ? 'rehome' : 'buy'} ${pet_breed || petType}, ${countryName}, ${region}, ${city}, ${isAdoption ? 'pet rescue, pet adoption, rehome pet' : 'pet breeder, purebred, pedigree'}`} />

                {/* Open Graph Meta Tags */}
                <meta property="og:title" content={`${listingAction} ${pet_breed || petType} ${title} - ${listingType} in ${locationString || 'your area'}`} />
                <meta property="og:description" content={description || `${listingAction} this beautiful ${pet_breed || petType} ${listingType} through Pets Home App.`} />
                {main_image && <meta property="og:image" content={main_image} />}
                <meta property="og:type" content="product" />
                <meta property="og:url" content={`https://www.petshome.app${canonicalPath}`} />

                {/* Twitter Card Meta Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${listingAction} ${pet_breed || petType} ${title} - ${listingType}`} />
                <meta name="twitter:description" content={description || `${listingAction} this beautiful ${pet_breed || petType} ${listingType} through Pets Home App.`} />
                {main_image && <meta name="twitter:image" content={main_image} />}

                {/* Geo Meta Tags for enhanced location SEO */}
                {countryName && <meta name="geo.country" content={countryName} />}
                {region && <meta name="geo.region" content={region} />}
                {city && <meta name="geo.placename" content={city} />}

                {/* Additional meta tags for sales/adoption focus */}
                <meta name="robots" content="index, follow" />
                <meta property="product:price:amount" content={asking_price} />
                <meta property="product:price:currency" content="USD" />
                <meta property="product:availability" content="in stock" />
                <meta property="product:condition" content="new" />

                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": isAdoption ? "Service" : "Product",
                        "name": `${pet_breed || petType} ${title}`,
                        "description": description || `${listingAction} this beautiful ${pet_breed || petType} ${listingType} through Pets Home App.`,
                        "image": main_image || '',
                        "offers": {
                            "@type": "Offer",
                            "priceCurrency": "USD",
                            "price": asking_price || '0',
                            "availability": "https://schema.org/InStock",
                            "itemCondition": "https://schema.org/NewCondition"
                        },
                        "category": pet_breed || petType,
                        "brand": {
                            "@type": "Brand",
                            "name": "Pets Home App"
                        },
                        "additionalProperty": [
                            {
                                "@type": "PropertyValue",
                                "name": "Location",
                                "value": locationString
                            },
                            {
                                "@type": "PropertyValue",
                                "name": "PetType",
                                "value": petType
                            },
                            {
                                "@type": "PropertyValue",
                                "name": "Breed",
                                "value": pet_breed || "Not specified"
                            },
                            {
                                "@type": "PropertyValue",
                                "name": "ListingType",
                                "value": isAdoption ? "Adoption/Rehoming" : "For Sale"
                            }
                        ]
                    })}
                </script>
            </Helmet>

            <Container className="PetDetail container-fluid">
                <Breadcrumb className="pet-breadcrumb">
                    <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/${lang}` }}>
                        Home
                    </Breadcrumb.Item>

                    {countryName && countryName.trim() && (
                        <Breadcrumb.Item
                            linkAs={Link}
                            linkProps={{ to: `/${lang}/pet-classified-ads/${toSlug(countryName)}` }}
                        >
                            {countryName}
                        </Breadcrumb.Item>
                    )}

                    {region && region.trim() && (
                        <Breadcrumb.Item
                            linkAs={Link}
                            linkProps={{
                                to: `/${lang}/pet-classified-ads/${toSlug(countryName)}/${toSlug(region)}`
                            }}
                        >
                            {region}
                        </Breadcrumb.Item>
                    )}

                    {city && city.trim() && (
                        <Breadcrumb.Item
                            linkAs={Link}
                            linkProps={{
                                to: `/${lang}/pet-classified-ads/${toSlug(countryName)}/${toSlug(region)}/${toSlug(city)}`
                            }}
                        >
                            {city}
                        </Breadcrumb.Item>
                    )}

                    <Breadcrumb.Item active>{title}</Breadcrumb.Item>
                </Breadcrumb>
                <header className="p-2 text-center">
                    {/* Primary H1 for SEO: Use the pet title with sale/adoption keywords and location info */}
                    <h1 className="display-8 text-dark">
                        {listingAction} {pet_breed ? `${pet_breed} ` : ''}{title}
                        {locationString ? ` in ${locationString}` : ''}
                    </h1>
                    {/* Status badge to highlight adoption/sale */}
                    <div className="mb-3">
                        <Badge bg={isAdoption ? "success" : "primary"} className="p-2">
                            <FontAwesomeIcon icon={isAdoption ? faHeart : faTag} className="me-1" />
                            {isAdoption ? "Available for Adoption" : "For Sale"}
                        </Badge>
                        {petType !== 'pet' && (
                            <Badge bg="info" className="p-2 ms-2">
                                <FontAwesomeIcon icon={faPaw} className="me-1" />
                                {petType.charAt(0).toUpperCase() + petType.slice(1)}
                            </Badge>
                        )}
                    </div>


                </header>

                <Card className="pet-card" style={{ margin: '20px auto' }}>
                    {main_image && (
                        <Card.Img
                            variant="top"
                            src={main_image}
                            alt={`${listingAction} ${pet_breed || petType} ${title} ${listingType} in ${locationString || 'your area'}`}
                            className="pet-image"
                        />
                    )}
                    <Card.Body>
                        <h2 className="text-dark">
                            {pet_breed ? `${pet_breed} - ${title}` : title} {locationString ? `in ${locationString}` : ''}
                        </h2>
                        <Card.Text>
                            <strong>Type:</strong> {petType.charAt(0).toUpperCase() + petType.slice(1)}
                        </Card.Text>
                        {pet_breed && (
                            <Card.Text>
                                <strong>Breed:</strong> {pet_breed}
                            </Card.Text>
                        )}
                        {locationString && (
                            <Card.Text>
                                <strong>Location:</strong> {locationString}
                            </Card.Text>
                        )}
                        <Card.Text>
                            <strong>Description:</strong> {description || 'No description available.'}
                        </Card.Text>
                        <Card.Text>
                            <strong>{isAdoption ? 'Adoption Fee' : 'Price'}:</strong> ${asking_price}
                        </Card.Text>

                        {/* Call to action button with adoption/sale wording */}


                        <Container className="container-fluid p-4">
                            <Row>
                                <Col md={12} className="p-4">
                                    <h4 className="text-center font-weight-bold"> {isAdoption ? `Adopt This ${pet_breed || petType}` : `Purchase This ${pet_breed || petType}`}</h4>
                                    <h5 className="text-center font-weight-bold">{t('global.h5_dwonload')}</h5>
                                    <div className="d-flex justify-content-center">
                                        <a href="https://play.google.com/store/apps/details?id=com.app.petshome" target="_blank" rel="noopener noreferrer" className="mr-3" title="Download Pets Home App from Google Play Store">
                                            <img style={{ width: '180px', height: 'auto', marginRight: '10px' }} src="/google-play-badge.svg" alt="Download Pets Home App on Google Play" />
                                        </a>
                                        <a href="https://apps.apple.com/app/apple-store/id1549828591?pt=581834&ct=product&mt=8" target="_blank" rel="noopener noreferrer" title="Download Pets Home App from Apple App Store">
                                            <img style={{ width: '180px', height: 'auto' }} src="/app-store-badge.svg" alt="Download Pets Home App on App Store" />
                                        </a>
                                    </div>
                                    <div className="d-flex justify-content-center">
                                        <div>
                                            <FontAwesomeIcon icon={faStar} color="gold" />
                                            <FontAwesomeIcon icon={faStar} color="gold" />
                                            <FontAwesomeIcon icon={faStar} color="gold" />
                                            <FontAwesomeIcon icon={faStar} color="gold" />
                                            <FontAwesomeIcon icon={faStar} color="gold" />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Container>

                        {/* SEO-rich text about adoption/sale process */}
                        <Card.Text className="mt-3 bg-light p-3 rounded">
                            <strong>{isAdoption ? 'Adoption' : 'Purchase'} Information:</strong><br />
                            {isAdoption ?
                                `This ${pet_breed || petType} is available for adoption in ${locationString || 'your area'}. Adoption helps rescue pets find loving forever homes. The adoption process typically includes an application, meet and greet, and adoption fee to cover medical costs.` :
                                `This ${pet_breed || petType} is available for purchase in ${locationString || 'your area'}. When buying a pet, ensure you're getting a healthy, well-socialized animal from a reputable source. `
                            }
                        </Card.Text>
                    </Card.Body>
                </Card>

                {/* Related Variations Section for SEO */}
                {relatedVariations.length > 0 && (
                    <section className="related-variations-section mt-4 mb-4">
                        <h2 className="text-center mb-3">Related {petType.charAt(0).toUpperCase() + petType.slice(1)} Listings</h2>
                        <div className="d-flex flex-wrap justify-content-center">
                            {relatedVariations.map((variation, index) => (
                                <Link
                                    key={index}
                                    to={`/${lang}${regionUrl || countryUrl || '/'}`}
                                    className="m-1 p-2 bg-light rounded text-decoration-none"
                                >
                                    <FontAwesomeIcon icon={faPaw} className="me-1" /> {variation}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* People Also Search For Section */}
                <section className="related-search-section mt-4 mb-4">
                    <h2 className="text-center mb-3">People Also Search For</h2>
                    <div className="related-search-terms d-flex flex-wrap justify-content-center">
                        {relatedSearchTerms.map((term, index) => {
                            // Create location-based search URLs similar to your LocationLinks component
                            let searchPath;
                            if (term.toLowerCase().includes('adopt') || term.toLowerCase().includes('rescue')) {
                                searchPath = regionUrl || countryUrl || '/';
                            } else if (term.toLowerCase().includes('near me')) {
                                searchPath = cityUrl || regionUrl || '/';
                            } else {
                                searchPath = regionUrl || countryUrl || '/';
                            }

                            return (
                                <Link
                                    key={index}
                                    to={`/${lang}${searchPath}`}
                                    className="related-search-term m-1 p-2 bg-light rounded text-decoration-none"
                                >
                                    <FontAwesomeIcon icon={faSearch} className="me-1" /> {term}
                                </Link>
                            );
                        })}
                    </div>
                </section>

                {/* Related Pets Section */}
                {relatedPets.length > 0 && (
                    <section className="related-pets-section mt-4 mb-4">
                        <h2 className="text-center mb-3">
                            {isAdoption ? `More Pets for Adoption in ${region || countryName}` : `More Pets for Sale in ${region || countryName}`}
                        </h2>
                        <Row>
                            {relatedPets.map(relatedPet => (
                                <Col key={relatedPet.id} xs={12} sm={6} md={4} lg={4} className="mb-3">
                                    <Card className="h-100 related-pet-card">
                                        {relatedPet.main_image && (
                                            <Card.Img
                                                variant="top"
                                                src={relatedPet.main_image}
                                                alt={`${relatedPet.asking_price <= 300 ? 'Adopt' : 'Buy'} ${relatedPet.pet_breed || 'pet'} ${relatedPet.title} in ${relatedPet.city || relatedPet.region || relatedPet.countryName}`}
                                                style={{ height: '200px', objectFit: 'cover' }}
                                            />
                                        )}
                                        <Card.Body className="d-flex flex-column">
                                            <Card.Title>{relatedPet.title}</Card.Title>
                                            <Badge
                                                bg={relatedPet.asking_price <= 300 ? "success" : "primary"}
                                                className="mb-2"
                                            >
                                                {relatedPet.asking_price <= 300 ? "For Adoption" : "For Sale"}
                                            </Badge>
                                            <Card.Text>
                                                {relatedPet.pet_breed && (
                                                    <><strong>Breed:</strong> {relatedPet.pet_breed}<br /></>
                                                )}
                                                <strong>Location:</strong> {relatedPet.city ? `${relatedPet.city}, ` : ''}
                                                {relatedPet.region || relatedPet.countryName}
                                                <br />
                                                <strong>{relatedPet.asking_price <= 300 ? 'Adoption Fee' : 'Price'}:</strong> ${relatedPet.asking_price}
                                            </Card.Text>
                                            <div className="mt-auto">
                                                <Link
                                                    to={`/${lang}/pet/${relatedPet.id}/${toSlug(relatedPet.title)}`}
                                                    className="btn btn-primary w-100"
                                                >
                                                    View Details
                                                </Link>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                        <div className="text-center mt-3">
                            {regionUrl && (
                                <Link
                                    to={`/${lang}${regionUrl}`}
                                    className="btn btn-outline-primary"
                                >
                                    See More {isAdoption ? 'Adoptable Pets' : 'Pets for Sale'} in {region || countryName}
                                </Link>
                            )}
                        </div>
                    </section>
                )}

                {/* Additional Location Links - Similar to your LocationLinks component */}
                <section className="browse-by-location mt-4 mb-4">
                    <h2 className="text-center mb-3">Browse {isAdoption ? 'Adoptable Pets' : 'Pets for Sale'} by Location</h2>
                    <div className="d-flex flex-wrap justify-content-center">
                        {countryUrl && (
                            <Link
                                to={`/${lang}${countryUrl}`}
                                className="m-1 p-2 bg-light rounded text-decoration-none"
                            >
                                All Pets in {countryName}
                            </Link>
                        )}
                        {regionUrl && (
                            <Link
                                to={`/${lang}${regionUrl}`}
                                className="m-1 p-2 bg-light rounded text-decoration-none"
                            >
                                All Pets in {region}
                            </Link>
                        )}
                        {cityUrl && (
                            <Link
                                to={`/${lang}${cityUrl}`}
                                className="m-1 p-2 bg-light rounded text-decoration-none"
                            >
                                All Pets in {city}
                            </Link>
                        )}
                    </div>
                </section>
            </Container>
        </>
    );
}

export default PetDetail;